import { memo, useEffect, useMemo, useState } from "react";
import i18n from "../i18n";
import styled from "styled-components";

function getNumericValue(...values) {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return 0;
}

function getSeatCount(item) {
  const seats = getNumericValue(
    item?.number_of_seats,
    item?.numberOfSeats,
    item?.seats,
  );

  return seats || 4;
}

function getTableShape(item) {
  const explicitShape = String(item?.round_or_square || "").toLowerCase();

  if (explicitShape.includes("round")) return "round";
  if (explicitShape.includes("circle")) return "round";

  const width = Number(item?.width) || 50;
  const height = Number(item?.height) || 50;

  if (width > height + 16) return "horizontal";
  if (height > width + 16) return "vertical";

  return "square";
}

function getTableState({
  mode,
  isSelected,
  isReservationSelected,
  tableStatus,
}) {
  if (mode === "reservation") {
    if (isReservationSelected && String(tableStatus) === "1") {
      return "selectedOccupied";
    }

    if (isReservationSelected) return "selected";
    if (String(tableStatus) === "1") return "occupied";
    return "free";
  }

  if (isSelected) return "setupSelected";
  return "setup";
}

function getPalette(state) {
  const palettes = {
    free: {
      tableBg: "#F8F6F2",
      tableBorder: "#D7D1C7",
      chairBg: "#F8F6F2",
      chairBorder: "#D7D1C7",
      text: "#6F6F6F",
      outline: "transparent",
      shadow: "0 2px 6px rgba(75, 51, 53, 0.08)",
    },
    occupied: {
      tableBg: "#99B0AD",
      tableBorder: "#819A97",
      chairBg: "#99B0AD",
      chairBorder: "#819A97",
      text: "#FFFFFF",
      outline: "transparent",
      shadow: "0 2px 6px rgba(75, 51, 53, 0.10)",
    },
    selected: {
      tableBg: "#EE754C",
      tableBorder: "#D9643C",
      chairBg: "#EE754C",
      chairBorder: "#D9643C",
      text: "#FFFFFF",
      outline: "rgba(238, 117, 76, 0.20)",
      shadow: "0 4px 10px rgba(238, 117, 76, 0.18)",
    },
    selectedOccupied: {
      tableBg: "#EE561B",
      tableBorder: "#C94817",
      chairBg: "#EE561B",
      chairBorder: "#C94817",
      text: "#FFFFFF",
      outline: "rgba(238, 86, 27, 0.18)",
      shadow: "0 4px 10px rgba(238, 86, 27, 0.18)",
    },
    setupSelected: {
      tableBg: "#2F8654",
      tableBorder: "#267047",
      chairBg: "#2F8654",
      chairBorder: "#267047",
      text: "#FFFFFF",
      outline: "rgba(47, 134, 84, 0.18)",
      shadow: "0 4px 10px rgba(47, 134, 84, 0.18)",
    },
    setup: {
      tableBg: "#F8F6F2",
      tableBorder: "#D7D1C7",
      chairBg: "#F8F6F2",
      chairBorder: "#D7D1C7",
      text: "#6F6F6F",
      outline: "transparent",
      shadow: "0 2px 6px rgba(75, 51, 53, 0.08)",
    },
  };

  return palettes[state] || palettes.free;
}

function getChairPositions(seatCount, shape) {
  const safeSeatCount = Math.max(0, Math.min(Number(seatCount) || 0, 12));

  if (safeSeatCount <= 0) return [];

  if (shape === "round") {
    const count = Math.min(safeSeatCount, 6);

    return Array.from({ length: count }).map((_, index) => {
      const angle = (index / count) * Math.PI * 2 - Math.PI / 2;
      const radius = 28; // closer to table

      return {
        left: `${50 + Math.cos(angle) * radius}%`,
        top: `${50 + Math.sin(angle) * radius}%`,
        transform: `translate(-50%, -50%) rotate(${angle + Math.PI / 2}rad)`,
      };
    });
  }

  const positions = [];

  const addTop = (x) =>
    positions.push({
      left: `${x}%`,
      top: "18%",
      transform: "translate(-50%, -50%)",
    });

  const addBottom = (x) =>
    positions.push({
      left: `${x}%`,
      top: "82%",
      transform: "translate(-50%, -50%)",
    });

  const addLeft = (y) =>
    positions.push({
      left: "18%",
      top: `${y}%`,
      transform: "translate(-50%, -50%) rotate(90deg)",
    });

  const addRight = (y) =>
    positions.push({
      left: "82%",
      top: `${y}%`,
      transform: "translate(-50%, -50%) rotate(90deg)",
    });

  if (safeSeatCount === 1) {
    addBottom(50);
  } else if (safeSeatCount === 2) {
    addLeft(50);
    addRight(50);
  } else if (safeSeatCount === 3) {
    addTop(50);
    addLeft(50);
    addRight(50);
  } else if (safeSeatCount === 4) {
    addTop(50);
    addBottom(50);
    addLeft(50);
    addRight(50);
  } else if (safeSeatCount === 5) {
    addTop(36);
    addTop(64);
    addBottom(50);
    addLeft(50);
    addRight(50);
  } else if (safeSeatCount === 6) {
    addTop(36);
    addTop(64);
    addBottom(36);
    addBottom(64);
    addLeft(50);
    addRight(50);
  } else if (safeSeatCount === 7) {
    addTop(30);
    addTop(50);
    addTop(70);
    addBottom(36);
    addBottom(64);
    addLeft(50);
    addRight(50);
  } else {
    addTop(28);
    addTop(50);
    addTop(72);
    addBottom(28);
    addBottom(50);
    addBottom(72);
    addLeft(36);
    addRight(36);

    if (safeSeatCount >= 9) addLeft(64);
    if (safeSeatCount >= 10) addRight(64);
    if (safeSeatCount >= 11) addTop(86);
    if (safeSeatCount >= 12) addBottom(86);
  }

  return positions.slice(0, safeSeatCount);
}

const TableRoot = styled.div`
  position: relative;
  width: ${({ item }) => Number(item?.width) || 50}px;
  height: ${({ item }) => Number(item?.height) || 50}px;
  min-width: 38px;
  min-height: 38px;
  cursor: ${({ mode }) => (mode === "reservation" ? "pointer" : "move")};
  user-select: none;
  transform: rotate(${({ item }) => Number(item?.rotation) || 0}deg);
  transition:
    transform 0.14s ease,
    filter 0.14s ease;

  &:hover {
    transform: rotate(${({ item }) => Number(item?.rotation) || 0}deg)
      scale(1.03);
    filter: brightness(1.01);
  }
`;

const SelectionHalo = styled.div`
  position: absolute;
  inset: -2px; /* smaller highlight area */
  border-radius: ${({ shape }) => (shape === "round" ? "16px" : "10px")};
  background: ${({ palette }) => palette.outline};
  pointer-events: none;
  z-index: 0;
`;

const TableSurface = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: ${({ shape }) => {
    if (shape === "horizontal") return "74%";
    if (shape === "vertical") return "56%";
    return "64%";
  }};
  height: ${({ shape }) => {
    if (shape === "horizontal") return "48%";
    if (shape === "vertical") return "74%";
    return "64%";
  }};
  transform: translate(-50%, -50%);
  border-radius: ${({ shape }) =>
    shape === "round" ? "14px" : "8px"}; /* less round */
  border: 1px solid ${({ palette }) => palette.tableBorder};
  background: ${({ palette }) => palette.tableBg};
  box-shadow: ${({ palette }) => palette.shadow};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
`;

const TableLabel = styled.div`
  color: ${({ palette }) => palette.text};
  font-size: ${({ compact }) => (compact ? "0.68rem" : "0.78rem")};
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
`;

const Chair = styled.div`
  position: absolute;
  width: ${({ compact }) => (compact ? "8px" : "10px")}; /* chubbier */
  height: ${({ compact }) => (compact ? "9px" : "11px")}; /* chubbier */
  border-radius: 6px; /* less round */
  border: 1px solid ${({ palette }) => palette.chairBorder};
  background: ${({ palette }) => palette.chairBg};
  z-index: 1;
`;

export const Table = memo(function Table({
  table_name,
  preview,
  id,
  selectedItem,
  item,
  mode = "setup",
  tableStatus = "0",
  isReservationSelected = false,
  onTableClick,
  tooltipText,
}) {
  const [isSelected, setIsSelected] = useState(false);

  useEffect(() => {
    if (selectedItem && selectedItem.id === item?.id) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  }, [selectedItem, item?.id]);

  const shape = useMemo(() => getTableShape(item), [item]);
  const seatCount = useMemo(() => getSeatCount(item), [item]);

  const state = getTableState({
    mode,
    isSelected,
    isReservationSelected,
    tableStatus,
  });

  const palette = getPalette(state);

  const chairPositions = useMemo(
    () => getChairPositions(seatCount, shape),
    [seatCount, shape],
  );

  const width = Number(item?.width) || 50;
  const height = Number(item?.height) || 50;
  const compact = width < 55 || height < 55;

  const label = table_name || item?.table_number || id;

  const handleClick = () => {
    if (typeof onTableClick === "function") {
      onTableClick(item);
    }
  };

  if (!item) return null;

  return (
    <TableRoot
      role={preview ? "presentation" : "button"}
      aria-label={i18n.t("ui.i18nFinalCleanup.tableLabel", { label })}
      item={item}
      mode={mode}
      onClick={handleClick}
      title={tooltipText || i18n.t("ui.i18nFinalCleanup.tableLabel", { label })}
    >
      <SelectionHalo shape={shape} palette={palette} />

      {chairPositions.map((position, index) => (
        <Chair
          key={`${item.id || id}-chair-${index}`}
          palette={palette}
          compact={compact}
          style={position}
        />
      ))}

      <TableSurface shape={shape} palette={palette}>
        <TableLabel palette={palette} compact={compact}>
          {label}
        </TableLabel>
      </TableSurface>
    </TableRoot>
  );
});
